import { IconButton } from "@components/button";
import { getIcon } from "@components/icon";
import { ISelectionChangeArgs } from "@components/inputs/select/Select.types";
import UserIconSelect from "@components/inputs/select/UserIconSelect";
import { EntityTypeName } from "@odata/GeneratedEntityTypes";
import { WebSocketMessageTypeCode } from "@odata/GeneratedEnums";
import { WithOData, withOData } from "@odata/withOData";
import { currentUserIsCustomer } from "@pages/admin/users/Users.utils";
import {
    checkChatMessageNotification,
    getLinkPropertyByEntityType,
    IChatOperationArgs
} from "@pages/chats/Chats.utils";
import customFetch from "@utils/customFetch";
import { isDefined } from "@utils/general";
import { capitalize } from "@utils/string";
import { TWebsocketMessage } from "@utils/websocketManager/Websocket.types";
import WebsocketManager from "@utils/websocketManager/WebsocketManager";
import { ChatOperationType, ContextEvents, IAppContext } from "contexts/appContext/AppContext.types";
import i18next from "i18next";
import React from 'react';
import { FormStorage } from "views/formView/FormStorage";

import { GET_CHATS_COUNT } from "../../constants";
import { MessageCount, MessageCountWrapper, UserIconSelectWrapper } from "./chatUserSelect.styles";
import { logger } from "@utils/log";
import TestIds from "../../testIds";

interface IProps extends WithOData {
    isPlainButton?: boolean;
    documentId?: string;

    context: IAppContext;
    storage?: FormStorage;
    entityType: EntityTypeName;

    isActive?: boolean;
    onChange: (args?: ISelectionChangeArgs) => void;
    hasCustomMessages?: boolean;
}

interface IState {
    unreadMessagesCount?: number;
    chatCount?: number;
    isLoaded?: boolean;
}

class ChatUserSelect extends React.Component <IProps, IState> {
    _cancelWebsocketSubscription: () => void;

    state = {
        unreadMessagesCount: 0,
        chatCount: 0,
        isLoaded: false
    }

    get IsPlainButton() {
        return this.props.hasCustomMessages === true || this.props.isPlainButton || (!!this.EntityType && !!this.DocumentId && this.state.chatCount > 0);
    }

    get EntityType(): EntityTypeName {
        return this.props.entityType || this.props.storage?.data?.bindingContext?.getEntityType().getName() as EntityTypeName;
    }

    get shouldUseCustomMessages() {
        return isDefined(this.props.hasCustomMessages);
    }

    get DocumentId() {
        return this.props.documentId;
    }

    get HasChat() {
        return this.state.chatCount > 0 || this.props.hasCustomMessages === true;
    }

    get DraftId() {
        if (this.props.storage) {
            const draftProp = this.props.storage?.data.definition.draftDef.draftProperty;
            const entity = this.props.storage?.data.entity;
            return entity[draftProp]?.Id;
        }

        return undefined;
    }

    get Context() {
        return this.props.context || this.props.storage?.context;
    }

    get IsCustomer() {
        return currentUserIsCustomer(this.Context);
    }

    constructor(props: IProps, context: IAppContext) {
        super(props);

        this._cancelWebsocketSubscription = WebsocketManager.subscribe({
            callback: this.handleChatNotification,
            types: [WebSocketMessageTypeCode.Notification]
        });
    }


    componentDidMount = () => {
        this.Context.eventEmitter.on(ContextEvents.ChatOperation, this.chatOperation);

        this.checkMessageData();
    }

    componentWillUnmount() {
        this.Context.eventEmitter.off(ContextEvents.ChatOperation, this.chatOperation);
        this._cancelWebsocketSubscription();
    }

    chatOperation = (args: IChatOperationArgs) => {
        if (args.type === ChatOperationType.Create) {
            const id = args.documentBc?.getKey() ?? args.id;
            if (this.DocumentId === id && this.EntityType === args.documentBc?.getEntityType().getName()) {
                if (this.state.chatCount === 0) {
                    this.setState({
                        chatCount: 1
                    });
                }
            }
        }

        if (args.type === ChatOperationType.Read) {
            this.setState({
                unreadMessagesCount: 0
            });
        }
    }

    handleChatNotification = async (message: TWebsocketMessage) => {
        const data = await checkChatMessageNotification(message, this.props.oData);
        if (data.LinkedEntityId === this.DocumentId && data.LinkedEntityType === this.EntityType) {
            if (data.isChat) {
                this.setState((state) => ({
                    chatCount: state.chatCount + 1,
                }));
            }

            if (data.isChatMessage) {
                this.setState((state) => ({
                    unreadMessagesCount: state.unreadMessagesCount + 1,
                }));
            }
        }
    }

    checkMessageData = async () => {
        if (!this.shouldUseCustomMessages) {
            const isDraft = isDefined(this.DraftId);
            const etWithDraft = isDraft ? getLinkPropertyByEntityType(this.EntityType as EntityTypeName, isDraft) : this.EntityType;
            const id = this.DraftId ?? this.DocumentId;
            const url = `${GET_CHATS_COUNT}/${etWithDraft}/${id}`;

            let chatCount: number,
                unreadMessagesCount: number;

            try {
                const response = await customFetch(url);
                if (response?.ok) {
                    const data = await response.json();
                    unreadMessagesCount = data?.UnreadMessagesCount;
                    chatCount = data?.ChatsCount;
                }
            } catch (error) {
                // if we fail to load correct data, set chatCount = 1 even tho it is not true
                // that will force to open dialog with table (not form) and hopefully it will load correct data in the dialog
                chatCount = 1;
                logger.error("Error loading chat data:", error);
            }

            this.setState({
                isLoaded: true,
                unreadMessagesCount,
                chatCount: chatCount || 0
            });
        }
    }

    handleChange = (args?: ISelectionChangeArgs) => {
        this.props.onChange(args);
    }

    handleButtonChange = () => {
        this.props.onChange();
    }

    renderComponent = () => {
        const Icon = getIcon(this.HasChat ? "ChatFilled" : "Chat");
        const isDisabled = !this.state.isLoaded && !this.shouldUseCustomMessages;

        if (this.IsPlainButton) {
            return (
                <IconButton
                    isTransparent
                    title={capitalize(i18next.t("Common:Form.Chat"))}
                    onClick={this.handleButtonChange}
                    isActive={this.props.isActive}
                    isDisabled={isDisabled}>
                    <Icon/>
                </IconButton>
            );
        }

        const myId = this.Context?.getData().userSettings?.Id;

        return <UserIconSelect
            skippedUserId={myId?.toString()}
            iconName={this.HasChat ? "ChatFilled" : "Chat"}
            isDisabled={isDisabled}
            isCustomer={this.IsCustomer}
            isActive={this.props.isActive}
            onChange={this.handleChange}
        />;
    }

    render() {
        return (
            <UserIconSelectWrapper data-testid={TestIds.Icon}>
                {this.state.unreadMessagesCount > 0 &&
                    <MessageCountWrapper>
                        <MessageCount>{this.state.unreadMessagesCount}</MessageCount>
                    </MessageCountWrapper>
                }
                {this.renderComponent()}
            </UserIconSelectWrapper>
        );

    }
}

export default withOData(ChatUserSelect);

